import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, TrendingUp, Dumbbell, ChevronRight, Scale, Activity, Plus, X, Flame, Target, Zap, Shield } from 'lucide-react';
import axios from 'axios';
import { API, getLocalDateString } from '../App';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import HamburgerMenu from '../components/HamburgerMenu';
import ProductRecommendation from '../components/ProductRecommendation';
import { getRecommendations } from '../data/armoryProducts';
import { getWorkoutTypeLabel } from '../utils/workoutTypes';

const Dashboard = ({ user, workoutType, onLogout, onChangeWorkoutType }) => {
  const navigate = useNavigate();
  const [todayPlan, setTodayPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [weightStats, setWeightStats] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [newWeight, setNewWeight] = useState('');
  const [savingWeight, setSavingWeight] = useState(false);
  const [streakData, setStreakData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [workoutType]);  // Re-fetch when workout type changes

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get recent workouts
      const historyResponse = await axios.get(`${API}/workouts/history?limit=10`);
      setRecentWorkouts(historyResponse.data.history || []);

      // Try to get today's plan for the current workout type
      const today = getLocalDateString();
      try {
        const planResponse = await axios.get(`${API}/workouts/plan/${today}?workout_type=${workoutType}`);
        // Backend returns {exists: false, plan: null} when no plan saved
        if (planResponse.data && planResponse.data.exists === false) {
          setTodayPlan(null);
        } else {
          setTodayPlan(planResponse.data);
        }
      } catch (error) {
        setTodayPlan(null);
      }

      // Get weight statistics
      try {
        const weightResponse = await axios.get(`${API}/weight/statistics`);
        setWeightStats(weightResponse.data);
      } catch (error) {
        console.error('Error loading weight stats:', error);
      }

      // Get premium status
      try {
        const statusResponse = await axios.get(`${API}/premium/status`);
        setPremiumStatus(statusResponse.data);
      } catch (error) {
        console.error('Error loading premium status:', error);
      }

      // Get streak data
      try {
        const streakResponse = await axios.get(`${API}/progress/streaks`);
        setStreakData(streakResponse.data);
      } catch (error) {
        console.error('Error loading streaks:', error);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogWeight = async () => {
    if (!newWeight || isNaN(parseFloat(newWeight))) {
      toast.error('Please enter a valid weight');
      return;
    }

    setSavingWeight(true);
    try {
      await axios.post(`${API}/weight/log`, {
        weight: parseFloat(newWeight),
        unit: user?.weight_unit || 'lbs'
      });
      toast.success('Weight logged successfully!');
      setShowWeightModal(false);
      setNewWeight('');
      // Refresh weight stats
      const weightResponse = await axios.get(`${API}/weight/statistics`);
      setWeightStats(weightResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log weight');
    } finally {
      setSavingWeight(false);
    }
  };

  const getFocusColor = (focus) => {
    const colors = {
      'back/biceps': 'from-blood-700 to-blood-900',
      'chest/triceps': 'from-blood-600 to-blood-800',
      'legs/glutes': 'from-blood-800 to-stone-900',
      'shoulders/abs': 'from-blood-700 to-blood-900',
      'full_body/cardio': 'from-blood-900 to-stone-950',
      'cardio': 'from-blood-900 to-stone-950',
      'core': 'from-blood-700 to-stone-800',
      'yoga': 'from-stone-700 to-stone-900',
      'pilates': 'from-stone-700 to-stone-900',
      'yoga/pilates': 'from-stone-700 to-stone-900',
      'rest': 'from-stone-800 to-stone-950'
    };
    return colors[focus] || 'from-blood-700 to-blood-900';
  };

  const getWeeklySchedule = () => {
    // Yoga/Pilates focused schedule (no weight training)
    if (workoutType === 'yoga_pilates') {
      return [
        { day: 'Monday', focus: 'yoga_strength', emoji: '☯️', label: 'Power Yoga' },
        { day: 'Tuesday', focus: 'cardio', emoji: '🏃', label: 'Cardio' },
        { day: 'Wednesday', focus: 'pilates_core', emoji: '💪', label: 'Core Pilates' },
        { day: 'Thursday', focus: 'yoga_flexibility', emoji: '🧘', label: 'Flexibility Yoga', isRest: true },
        { day: 'Friday', focus: 'cardio', emoji: '🔥', label: 'Cardio' },
        { day: 'Saturday', focus: 'yoga_balance', emoji: '🌸', label: 'Balance Yoga' },
        { day: 'Sunday', focus: 'pilates_recovery', emoji: '✨', label: 'Recovery Pilates', isRest: true }
      ];
    }
    
    // Traditional strength training schedule (home/gym)
    return [
      { day: 'Monday', focus: 'back/biceps', emoji: '⚔️', label: 'Back & Biceps' },
      { day: 'Tuesday', focus: 'chest/triceps', emoji: '🛡️', label: 'Chest & Triceps' },
      { day: 'Wednesday', focus: 'legs/glutes', emoji: '⚡', label: 'Legs & Glutes' },
      { day: 'Thursday', focus: 'yoga', emoji: '🧘', label: 'Yoga', isRest: true },
      { day: 'Friday', focus: 'shoulders/abs', emoji: '💪', label: 'Shoulders & Abs' },
      { day: 'Saturday', focus: 'pilates', emoji: '🧘', label: 'Pilates', isRest: true },
      { day: 'Sunday', focus: 'full_body/cardio', emoji: '🔥', label: 'Full Body & Cardio' }
    ];
  };

  const getCurrentDayIndex = () => {
    return new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  };

  const handleDayClick = (dayOffset) => {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);

    monday.setDate(today.getDate() + mondayOffset);

    const targetDate = new Date(monday);
    targetDate.setDate(monday.getDate() + dayOffset);
    targetDate.setHours(12, 0, 0, 0);

    // All scheduled sessions use the unified workout logger.
    // The backend determines whether the selected date is
    // strength, cardio, Yoga, or Pilates.
    navigate('/log-workout', {
      state: {
        date: targetDate.toISOString(),
      },
    });
  };

  // Filter workouts by type
  const workoutsByType = recentWorkouts.filter(w => w.workout_type === workoutType);
  const thisWeekWorkouts = workoutsByType.filter(w => {
    const workoutDate = new Date(w.date);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return workoutDate >= weekAgo;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black">
        <HamburgerMenu />
        <div className="flex items-center justify-center pt-20">
          <div className="text-blood-400 text-xl font-bold tracking-wider">LOADING ARENA...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20">
      <HamburgerMenu />
      
      <div className="max-w-7xl mx-auto px-4 py-6 animate-fade-in">
        {/* Trial Banner */}
        {premiumStatus?.trial_active && (
          <div className="bg-gradient-to-r from-blood-900/30 to-blood-800/30 border border-blood-700 rounded-lg p-3 mb-4">
            <p className="text-blood-300 font-semibold text-center tracking-wider text-sm">
              ⚔️ PREMIUM TRIAL: {premiumStatus.days_remaining} DAYS LEFT • 
              <button 
                onClick={() => navigate('/settings')}
                className="ml-2 underline hover:text-blood-200"
              >
                UPGRADE
              </button>
            </p>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 mb-1 tracking-wider" data-testid="dashboard-title">
            WORKOUT ARENA
          </h1>
          <div className="flex items-center justify-between">
            <p className="text-stone-500 uppercase tracking-widest text-xs font-semibold">
              {getWorkoutTypeLabel(workoutType)}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={onChangeWorkoutType}
              className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 text-xs"
              data-testid="change-workout-type-btn"
            >
              Switch Arena
            </Button>
          </div>
        </div>

        {/* Streak & Consistency Bar */}
        {streakData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="streak-bar">
            <Card className="glass p-3 border border-stone-800 text-center">
              <Flame className={`w-6 h-6 mx-auto mb-1 ${streakData.current_streak > 0 ? 'text-orange-400' : 'text-stone-600'}`} />
              <p className={`text-2xl font-bold ${streakData.current_streak > 0 ? 'text-orange-400' : 'text-stone-500'}`}>{streakData.current_streak}</p>
              <p className="text-stone-500 text-xs uppercase tracking-wider">Day Streak</p>
            </Card>
            <Card className="glass p-3 border border-stone-800 text-center">
              <Zap className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
              <p className="text-2xl font-bold text-yellow-500">{streakData.longest_streak}</p>
              <p className="text-stone-500 text-xs uppercase tracking-wider">Best Streak</p>
            </Card>
            <Card className="glass p-3 border border-stone-800 text-center">
              <Target className="w-6 h-6 mx-auto mb-1 text-blood-400" />
              <p className="text-2xl font-bold text-blood-400">{streakData.this_week}/{streakData.weekly_goal}</p>
              <p className="text-stone-500 text-xs uppercase tracking-wider">This Week</p>
            </Card>
            <Card className="glass p-3 border border-stone-800 text-center">
              <Dumbbell className="w-6 h-6 mx-auto mb-1 text-blood-400" />
              <p className="text-2xl font-bold text-blood-400">{streakData.this_month}</p>
              <p className="text-stone-500 text-xs uppercase tracking-wider">This Month</p>
            </Card>
          </div>
        )}

        {/* Statistics Card - Weight Tracker */}
        <Card className="glass p-4 md:p-6 mb-6 border border-stone-800">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-blood-400 flex items-center tracking-wider">
                  <Scale className="w-6 h-6 mr-3 text-blood-500" />
                  WEIGHT PROGRESS
                </h2>
                <Button
                  onClick={() => setShowWeightModal(true)}
                  size="sm"
                  className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-semibold"
                  data-testid="log-weight-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> LOG WEIGHT
                </Button>
              </div>
              {weightStats && weightStats.trend_data && weightStats.trend_data.length > 0 ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <LineChart data={weightStats.trend_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#737373" 
                        tick={{ fill: '#737373', fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        stroke="#737373" 
                        tick={{ fill: '#737373' }}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#171717', 
                          border: '1px solid #404040',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#a3a3a3' }}
                        itemStyle={{ color: '#dc2626' }}
                      />
                      {/* Goal line */}
                      {user.goal_weight && (
                        <Line 
                          type="monotone" 
                          dataKey="goal" 
                          stroke="#8b0000" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          name="Goal"
                        />
                      )}
                      {/* Actual weight line */}
                      <Line 
                        type="monotone" 
                        dataKey="weight" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        dot={{ fill: '#dc2626', r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                        name="Weight"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <div className="text-center">
                    <Scale className="w-16 h-16 text-stone-600 mx-auto mb-4 opacity-50" />
                    <p className="text-stone-400 mb-4">NO WEIGHT DATA YET</p>
                    <Button
                      onClick={() => setShowWeightModal(true)}
                      className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-semibold px-6 py-3 rounded-xl"
                      data-testid="log-first-weight-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> LOG YOUR FIRST WEIGHT
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="glass p-4 rounded-lg border border-stone-800">
                <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Current Weight</p>
                <p className="text-3xl font-bold text-blood-400">
                  {weightStats?.current_weight ? `${weightStats.current_weight} ${user.weight_unit || 'lbs'}` : '--'}
                </p>
              </div>
              <div className="glass p-4 rounded-lg border border-stone-800">
                <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Goal Weight</p>
                <p className="text-2xl font-bold text-blood-400">
                  {user.goal_weight ? `${user.goal_weight} ${user.weight_unit || 'lbs'}` : 'Not set'}
                </p>
              </div>
              <div className="glass p-4 rounded-lg border border-stone-800">
                <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Total Change</p>
                <p className={`text-2xl font-bold ${weightStats?.total_change < 0 ? 'text-blood-500' : 'text-blood-600'}`}>
                  {weightStats?.total_change ? `${weightStats.total_change > 0 ? '+' : ''}${weightStats.total_change} ${user.weight_unit || 'lbs'}` : '--'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass p-6 card-hover border border-stone-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">{workoutType === 'home' ? 'Home' : 'Gym'} Workouts</p>
                <p className="text-3xl font-bold text-blood-400" data-testid="total-workouts">{workoutsByType.length}</p>
              </div>
              <Activity className="w-12 h-12 text-blood-600" />
            </div>
          </Card>
          <Card className="glass p-6 card-hover border border-stone-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">This Week</p>
                <p className="text-3xl font-bold text-blood-400" data-testid="week-workouts">
                  {thisWeekWorkouts.length}
                </p>
              </div>
              <Calendar className="w-12 h-12 text-blood-600" />
            </div>
          </Card>
          <Card 
            className="glass p-6 card-hover cursor-pointer hover:scale-105 transition-transform border border-stone-800"
            onClick={() => navigate('/nutrition')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm mb-1 uppercase tracking-wider">Nutrition</p>
                <p className="text-lg font-bold text-blood-400">TRACK MACROS</p>
                <span className="text-xs text-blood-500 font-semibold">PREMIUM</span>
              </div>
              <div className="text-4xl">🔥</div>
            </div>
          </Card>
        </div>

        {/* Weekly Schedule Calendar */}
        <Card className="glass p-8 mb-8 border border-stone-800" data-testid="weekly-schedule">
          <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
            <Calendar className="w-6 h-6 mr-3 text-blood-500" />
            YOUR WEEKLY BATTLE PLAN
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {getWeeklySchedule().map((day, idx) => {
              const isToday = idx === getCurrentDayIndex();
              const cardClasses = isToday 
                ? 'glass p-6 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer ring-2 ring-blood-600 bg-blood-900/20 col-span-2 sm:col-span-1 border border-blood-700'
                : 'glass p-3 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer hover:bg-blood-950/20 border border-stone-800';
              
              return (
                <button
                  key={idx}
                  onClick={() => handleDayClick(idx)}
                  className={cardClasses}
                  data-testid={`schedule-day-${idx}`}
                >
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs font-semibold ${isToday ? 'text-blood-300' : 'text-stone-400'} tracking-wider`}>
                        {day.day.slice(0, 3).toUpperCase()}
                      </p>
                      {isToday && (
                        <span className="px-2 py-0.5 bg-blood-600 text-white text-xs rounded-full font-bold">
                          TODAY
                        </span>
                      )}
                    </div>
                    <div className={isToday ? 'text-5xl mb-3' : 'text-2xl mb-2'}>{day.emoji}</div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-lg bg-gradient-to-r ${getFocusColor(day.focus)} text-white text-xs font-semibold uppercase tracking-wide`}>
                      {day.isRest ? 'Active Rest' : day.focus.split('/').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('/')}
                    </div>
                    {isToday && todayPlan && (
                      <div className="mt-3 text-left">
                        <p className="text-stone-400 text-xs mb-1 uppercase">{todayPlan.exercises.length} exercises</p>
                        {todayPlan.exercises.slice(0, 2).map((ex, i) => (
                          <p key={i} className="text-blood-300 text-xs truncate">⚔ {ex.exercise_name}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-stone-400 text-sm mt-4 text-center tracking-wider">
            ⚔️ CLICK ANY DAY TO VIEW OR START THAT WORKOUT
          </p>
        </Card>

        {/* Recent Activity */}
        <Card className="glass p-8 border border-stone-800" data-testid="recent-activity-card">
          <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
            <Activity className="w-6 h-6 mr-3 text-blood-500" />
            RECENT BATTLES
          </h2>
          {recentWorkouts.length > 0 ? (
            <div className="space-y-4">
              {recentWorkouts.slice(0, 5).map((workout, idx) => (
                <div key={idx} className="glass p-4 rounded-lg border border-stone-800">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-blood-300 font-semibold tracking-wider">
                      {new Date(workout.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}
                    </p>
                    {workout.completed ? (
                      <span className="text-blood-500 text-sm font-semibold">⚔ COMPLETED</span>
                    ) : (
                      <span className="text-stone-400 text-sm font-semibold">IN PROGRESS</span>
                    )}
                  </div>
                  <p className="text-stone-400 text-sm uppercase tracking-wide">
                    {workout.exercises.length} exercises &bull; {getWorkoutTypeLabel(workout.workout_type)}
                  </p>
                </div>
              ))}
              <Button
                onClick={() => navigate('/progress')}
                variant="outline"
                className="w-full glass border-blood-700 text-blood-400 hover:bg-blood-950/50 mt-4 font-semibold tracking-wider"
                data-testid="view-all-btn"
              >
                VIEW ALL PROGRESS
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-stone-400 mb-4 uppercase tracking-wider">NO BATTLES LOGGED YET</p>
              <p className="text-stone-500 text-sm">START YOUR FIRST WORKOUT TO SEE YOUR PROGRESS HERE!</p>
            </div>
          )}
        </Card>

        {/* Contextual Product Recommendations - non-premium only */}
        {user?.tier !== 'premium' && (
          <Card className="glass p-4 mt-6 border border-stone-800" data-testid="dashboard-recommendations">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-500" />
                <p className="text-stone-400 text-sm font-semibold uppercase tracking-wider">Gear Up</p>
              </div>
              <button onClick={() => navigate('/armory')} className="text-blood-500 text-xs hover:text-blood-400">
                Armory →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {getRecommendations(
                workoutType === 'home' || workoutType === 'calisthenics'
                  ? ['home_workout', 'resistance-bands', 'protein']
                  : ['gym', 'strength_training', 'protein'],
                3
              ).map(p => (
                <ProductRecommendation key={p.id} product={p} variant="compact" />
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Weight Log Modal */}
      {showWeightModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowWeightModal(false)}>
          <Card 
            className="p-6 border border-stone-600 w-full max-w-sm animate-fade-in bg-stone-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-blood-400 tracking-wider flex items-center">
                <Scale className="w-5 h-5 mr-2" />
                LOG WEIGHT
              </h3>
              <button
                onClick={() => setShowWeightModal(false)}
                className="text-stone-400 hover:text-white transition-colors"
                data-testid="close-weight-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-stone-400 text-sm uppercase tracking-wider mb-2 block">
                  Current Weight ({user?.weight_unit || 'lbs'})
                </label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder={`Enter weight in ${user?.weight_unit || 'lbs'}`}
                  value={newWeight}
                  onChange={(e) => setNewWeight(e.target.value)}
                  className="bg-stone-900 border-stone-700 text-white text-lg py-6"
                  data-testid="weight-input"
                  autoFocus
                />
              </div>

              {weightStats?.current_weight && (
                <p className="text-stone-500 text-sm">
                  Previous: {weightStats.current_weight} {user?.weight_unit || 'lbs'}
                </p>
              )}

              <Button
                onClick={handleLogWeight}
                disabled={savingWeight || !newWeight}
                className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 tracking-wider disabled:opacity-50"
                data-testid="save-weight-btn"
              >
                {savingWeight ? 'SAVING...' : 'SAVE WEIGHT'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
